<template>
    <div class="card h-100 border-0 shadow-sm attach-bank-slip-card">
        <div class="card-header bg-white border-0 pt-3 pb-0 px-4">
            <h3 class="card-title text-uppercase font-weight-500 mb-0 d-flex align-items-center">
                <Icon icon-class="ui-file-02" size="4" class="text-primary mr-2" />
                {{ t('invoices-invoice_bank_slip') }}
            </h3>
        </div>
        
        <div class="card-body p-3 px-4 d-flex flex-column">
            <div v-if="invoice && invoice.bank_slip_url && !uploading" class="attached-container flex-grow-1 d-flex flex-column justify-content-center">
                <div class="attached-card p-3 rounded-xl border d-flex align-items-center">
                    <div class="pdf-icon-wrapper mr-3">
                        <Icon icon-class="ui-file-02" size="4" />
                    </div>
                    <div class="flex-grow-1">
                        <div class="d-flex align-items-center justify-content-between flex-wrap">
                            <div>
                                <h4 class="mb-0 font-weight-500 text-dark title-attached">{{ t('invoices-attached_bank_slip') }}</h4>
                                <p class="text-muted smallest mb-0 mt-1">{{ t('invoices-available_for_download') }}</p>
                            </div>
                            <div class="action-buttons d-flex align-items-center flex-wrap">
                                <a :href="invoice.bank_slip_url" target="_blank" class="action-btn view-btn mr-2">
                                    <Icon icon-class="ui-link-01" size="2" class="mr-1" /> {{ t('invoices-view') }}
                                </a>
                                <kv-button
                                    variant="text"
                                    class="action-btn mr-2"
                                    prepend-icon="ui-mail-01"
                                    :loading="sendingEmail"
                                    @click="sendInvoiceEmail"
                                >
                                    {{ t('invoices-send_invoice_email') }}
                                </kv-button>
                                <kv-button
                                    variant="text"
                                    class="action-btn delete-btn"
                                    prepend-icon="ui-trash-01"
                                    @click="openRemoveModal"
                                >
                                    {{ t('invoices-remove') }}
                                </kv-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="upload-container flex-grow-1 d-flex flex-column">

                <kv-card v-if="bankSlipResult" variant="soft" class="mb-3" content-class="p-3">
                    <template #text>
                        <div class="d-flex align-items-center mb-2">
                            <Icon icon-class="ui-check-circle" class="text-success mr-2" />
                            <span class="font-weight-600 text-dark">{{ t('invoices-bank_slip_generated_title') }}</span>
                        </div>
                        <p class="text-muted smallest mb-3">{{ t('invoices-bank_slip_generated_hint') }}</p>

                        <div v-if="bankSlipResult.expires_at" class="text-muted smallest mb-3">
                            {{ t('invoices-bank_slip_expires_at') }}:
                            <span class="text-dark font-weight-600">{{ new Date(bankSlipResult.expires_at).toLocaleDateString() }}</span>
                        </div>

                        <div class="d-flex flex-wrap gap-2">
                            <kv-button
                                v-if="bankSlipResult.voucher_url"
                                variant="outlined"
                                prepend-icon="ui-link-01"
                                size="small"
                                @click="openUrl(bankSlipResult.voucher_url)"
                            >
                                {{ t('invoices-bank_slip_view_voucher') }}
                            </kv-button>
                            
                            <kv-copy-button
                                v-if="bankSlipResult.payment_code"
                                variant="outlined"
                                size="small"
                                :value="bankSlipResult.payment_code"
                                :text="t('invoices-bank_slip_copy_code')"
                                :success-text="t('invoices-bank_slip_code_copied')"
                            />
                        </div>
                    </template>
                </kv-card>

                <div v-if="!bankSlipResult" class="mb-3">
                    <kv-button
                        variant="tonal"
                        prepend-icon="ui-bank-note-01"
                        full-width
                        :loading="bankSlipGenerating"
                        @click="generateBankSlip"
                    >
                        {{ t('invoices-generate_bank_slip') }}
                    </kv-button>
                    <p v-if="bankSlipGenerating" class="text-muted smallest mt-2 text-center">
                        <Icon icon-class="ui-loading-01" class="fa-spin mr-1" />
                        {{ t('invoices-bank_slip_generating') }}
                    </p>
                </div>

                <div class="divider-or d-flex align-items-center mb-3">
                    <hr class="flex-grow-1"><span class="px-2 text-muted smallest">ou</span><hr class="flex-grow-1">
                </div>

                <p class="text-muted smallest mb-3">
                    {{ t('invoices-upload_bank_slip_hint') }}
                </p>

                <div
                    class="upload-area p-4 text-center border-dashed rounded-xl flex-grow-1 d-flex flex-column align-items-center justify-content-center"
                    :class="{'is-dragover': isDragover, 'is-loading': uploading}"
                    @dragover.prevent="isDragover = true"
                    @dragleave.prevent="isDragover = false"
                    @drop.prevent="handleDrop"
                >
                    <div v-if="uploading" class="py-3">
                        <div class="spinner-custom mb-3"></div>
                        <p class="mb-0 text-dark font-weight-500">{{ t('invoices-sending_document') }}</p>
                        <p class="text-muted smallest">{{ t('invoices-wait_a_moment') }}</p>
                    </div>
                    <div v-else class="upload-content">
                        <div class="upload-icon-wrapper mb-3">
                            <Icon icon-class="ui-upload-02" size="6" />
                        </div>
                        <h4 class="mb-1 font-weight-500 text-dark title-upload">{{ t('invoices-drag_pdf_here') }}</h4>
                        <p class="text-muted small mb-3">{{ t('invoices-or_click_to_select') }}</p>

                        <kv-button variant="filled" prepend-icon="ui-upload-02" @click="$refs.fileInput.click()">
                            {{ t('invoices-select_file') }}
                        </kv-button>

                        <input
                            type="file"
                            ref="fileInput"
                            class="d-none"
                            accept="application/pdf"
                            @change="handleFileSelect"
                        >
                    </div>
                </div>
            </div>

            <p v-if="error" class="text-danger small mt-3 mb-0 text-center animate-shake">
                <Icon icon-class="ui-alert-circle" class="mr-1" /> {{ error }}
            </p>
        </div>

        <KvModal
            ref="removeBankSlipModal"
            id="remove-bank-slip-modal"
            @close="showConfirmModal = false"
        >
            <template #title>
                {{ t('invoices-remove_bank_slip_title') }}
            </template>
            <template #body>
                <div v-if="invoice.has_active_gateway_charge">
                    <div class="d-flex align-items-start mb-3">
                        <Icon icon-class="ui-alert-triangle" class="text-warning mr-2 mt-1 flex-shrink-0" />
                        <div>
                            <p class="mb-2 font-weight-600 text-dark">
                                {{ t('invoices-gateway_slip_cancel_warning_title') }}
                            </p>
                            <p class="mb-0 text-muted small">
                                {{ t('invoices-gateway_slip_cancel_warning_body') }}
                            </p>
                        </div>
                    </div>
                </div>
                <div v-else>
                    {{ t('invoices-remove_bank_slip_confirm') }}
                </div>
            </template>
            <template #footer="{ toggle }">
                <kv-button 
                    variant="outlined" 
                    @click="toggle(false)"
                >
                    {{ t('invoices-cancel') }}
                </kv-button>
                <kv-button 
                    variant="danger" 
                    :loading="removing"
                    @click="handleConfirmRemove"
                >
                    {{ invoice.has_active_gateway_charge ? t('invoices-yes_cancel_and_remove') : t('invoices-yes_remove') }}
                </kv-button>
            </template>
        </KvModal>
    </div>
</template>

<script>
import keviApi from '../../keviApi';
import KvModal from "../../components/modal/modal.vue";
import { extractApiError } from "../helpers";

export default {
    name: "invoice-attach-bank-slip",
    components: {
        KvModal
    },
    props: {
        invoice: {
            type: Object,
            required: true
        },
        invoiceId: {
            type: [String, Number],
            default: null
        }
    },
    computed: {
        resolvedInvoiceId() {
            if (this.invoiceId !== null && this.invoiceId !== undefined && String(this.invoiceId) !== '') {
                return String(this.invoiceId);
            }
            return this.invoice && this.invoice.id != null ? String(this.invoice.id) : '';
        }
    },
    data() {
        return {
            isDragover: false,
            uploading: false,
            removing: false,
            showConfirmModal: false,
            error: null,
            bankSlipGenerating: false,
            bankSlipResult: null,
            sendingEmail: false,
            codeCopied: false,
        };
    },
    methods: {
        async generateBankSlip() {
            if (!this.invoice.place_tax_id) {
                this.error = this.t('invoices-missing_tax_id_error');
                return;
            }
            if (!this.invoice.place_email) {
                this.error = this.t('invoices-missing_email_error');
                return;
            }
            if (!this.invoice.place_zip_code) {
                this.error = this.t('invoices-missing_cep_error');
                return;
            }
            if ((parseFloat(this.invoice.total) || 0) < 5) {
                this.error = this.t('invoices-minimum_amount_error');
                return;
            }

            this.error = null;
            this.bankSlipGenerating = true;
            this.bankSlipResult = null;

            try {
                const response = await this.$http.post(
                    `${keviApi.endpoint}invoices/${this.resolvedInvoiceId}/bank-slip`,
                    {},
                    { headers: keviApi.getAuthHeader() }
                );

                if (response.status === 200 && response.body.voucher_url) {
                    const message = response.body.email_sent
                        ? this.t('invoices-bank_slip_generated_email_sent')
                        : this.t('invoices-bank_slip_generation_success');
                    this.$store.dispatch('notification/success', message);
                    if (!response.body.email_sent) {
                        this.$store.dispatch('notification/warning', this.t('invoices-invoice_email_failed'));
                    }
                    this.$emit('uploaded', response.body.voucher_url);
                    this.$emit('refresh');
                    this.bankSlipResult = response.body;
                } else {
                    this.error = this._extractBankSlipError(response.body);
                }
            } catch (e) {
                const body = e && e.body ? e.body : (e && e.response && e.response.data ? e.response.data : null);
                if (body && body.error) {
                    this.error = this._extractBankSlipError(body);
                } else {
                    this.error = this.t('invoices-bank_slip_generation_error') + ' (Erro de conexão)';
                }
            } finally {
                this.bankSlipGenerating = false;
            }
        },
        _extractBankSlipError(body) {
            if (!body) return this.t('invoices-bank_slip_generation_error');
            
            let msg = body.error || this.t('invoices-bank_slip_generation_error');
            const parts = [];
            
            if (body.error_code) parts.push(`Código: ${body.error_code}`);
            if (body.decline_code) parts.push(`Recusa: ${body.decline_code}`);
            if (body.param) parts.push(`Campo: ${body.param}`);
            if (body.error_detail && body.error_detail !== body.error) parts.push(body.error_detail);
            
            if (parts.length > 0) {
                msg += ' (' + parts.join(' | ') + ')';
            }
            
            return msg;
        },
        async sendInvoiceEmail() {
            if (!this.invoice || !this.invoice.bank_slip_url || this.sendingEmail) return;
            this.sendingEmail = true;
            this.error = null;
            try {
                const response = await this.$http.post(
                    `${keviApi.endpoint}invoices/${this.resolvedInvoiceId}/bank-slip/email`,
                    { bank_slip_url: this.invoice.bank_slip_url },
                    { headers: keviApi.getAuthHeader() }
                );
                if (response.status === 200 && response.body.email_sent) {
                    this.$store.dispatch('notification/success', this.t('invoices-invoice_email_sent'));
                } else {
                    this.error = this.t('invoices-invoice_email_failed');
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-invoice_email_failed'));
            } finally {
                this.sendingEmail = false;
            }
        },
        async copyPaymentCode() {
            if (!this.bankSlipResult?.payment_code) return;
            try {
                await navigator.clipboard.writeText(this.bankSlipResult.payment_code);
                this.codeCopied = true;
                setTimeout(() => { this.codeCopied = false; }, 2500);
            } catch {
                this.error = 'Could not copy to clipboard.';
            }
        },
        openRemoveModal() {
            this.showConfirmModal = true;
            this.$nextTick(() => {
                this.$refs.removeBankSlipModal.open();
            });
        },
        handleDrop(e) {
            this.isDragover = false;
            const files = e.dataTransfer.files;
            if (files && files.length > 0) {
                this.uploadFile(files[0]);
            }
        },
        handleFileSelect(e) {
            const files = e.target.files;
            if (files && files.length > 0) {
                this.uploadFile(files[0]);
            }
        },
        async uploadFile(file) {
            this.error = null;
            
            if (file.type !== 'application/pdf') {
                this.error = this.t('invoices-only_pdf_error');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                this.error = this.t('invoices-max_size_error');
                return;
            }

            this.uploading = true;
            
            try {
                const formData = new FormData();
                const sanitizedName = this.sanitizeFilename(file.name);
                formData.append('bank_slip', file, sanitizedName);

                const response = await this.$http.post(
                    `${keviApi.endpoint}invoices/${this.resolvedInvoiceId}/bank-slips`,
                    formData,
                    { headers: keviApi.getAuthHeader() }
                );

                if (response.status === 200 && response.body.bank_slip_url) {
                    this.$emit('uploaded', response.body.bank_slip_url);
                    if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                } else {
                    this.error = extractApiError(response, this.t('invoices-upload_failure'));
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-upload_connection_error'));
            } finally {
                this.uploading = false;
            }
        },
        async handleConfirmRemove() {
            this.removing = true;
            this.error = null;
            
            try {
                const response = await this.$http.delete(
                    `${keviApi.endpoint}invoices/${this.resolvedInvoiceId}/bank-slips`,
                    { headers: keviApi.getAuthHeader() }
                );

                if (response.status === 200) {
                    this.$emit('uploaded', null);
                    this.showConfirmModal = false;
                    if (this.$refs.removeBankSlipModal) {
                        this.$refs.removeBankSlipModal.close();
                    }
                } else {
                    this.error = extractApiError(response, this.t('invoices-remove_failure'));
                }
            } catch (e) {
                this.error = extractApiError(e, this.t('invoices-remove_connection_error'));
            } finally {
                this.removing = false;
            }
        },
        openUrl(url) {
            if (url) {
                window.open(url, '_blank');
            }
        },
        sanitizeFilename(filename) {
            if (!filename) return 'bank_slip.pdf';
            
            const parts = filename.split('.');
            const ext = parts.length > 1 ? '.' + (parts.pop().toLowerCase()) : '.pdf';
            let name = parts.join('.');

            name = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            
            name = name.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
            
            return (name || 'bank_slip') + ext;
        }
    }
}
</script>

<style lang="scss" scoped>
.attach-bank-slip-card {
    border-radius: 20px;
    border: 1px solid #E5E7EB !important;
    background: #FFFFFF;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05) !important;
    }

    .card-title {
        font-size: 13px;
        color: #6B7280;
        letter-spacing: 0.1em;

        i {
            font-size: 16px;
            color: #2aaa4c;
        }
    }
}

.attached-card {
    background: #F9FAFB;
    border: 1px solid #E5E7EB !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    
    .pdf-icon-wrapper {
        width: 44px;
        height: 44px;
        background: #FFFFFF;
        color: #2aaa4c;
        border: 1px solid #E5E7EB;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.02);
    }
    
    .title-attached {
        font-size: 1rem;
    }

    .action-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        text-decoration: none;
        transition: all 0.2s ease;
        border: 1px solid transparent;
        cursor: pointer;
        background: transparent;

        &.view-btn {
            color: #3B82F6;
            background: #EFF6FF;
            &:hover { background: #DBEAFE; transform: translateY(-1px); }
        }

        &.delete-btn {
            color: #F04438 !important;
            background: transparent !important;
            border: 1px solid transparent !important;
            
            &:hover { 
                background: #FEF3F2 !important; 
                color: #DE3024 !important;
                border-color: #FEE4E2 !important;
                transform: translateY(-1px); 
            }

            :deep(.ui-icon), :deep(i) {
                color: inherit !important;
            }
        }
    }
}


.divider-or hr {
    border-color: #E5E7EB;
    margin: 0;
}

.gap-2 { gap: 0.5rem; }

.upload-area {
    border: 2px dashed #D1D5DB !important;
    background-color: #F9FAFB;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 160px;
    cursor: pointer;
    
    .upload-icon-wrapper {
        width: 64px;
        height: 64px;
        background: #FFFFFF;
        color: #9CA3AF;
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        font-size: 28px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.03);
        transition: all 0.3s ease;
    }
    
    .title-upload {
        font-size: 1.1rem;
    }

    &.is-dragover {
        border-color: #2aaa4c !important;
        background-color: rgba(42, 170, 76, 0.04);
        transform: scale(1.01);
        
        .upload-icon-wrapper {
            background-color: #2aaa4c;
            color: white;
            transform: translateY(-5px);
        }
    }
    
    &.is-loading {
        background-color: #FFFFFF;
        border-style: solid !important;
        cursor: not-allowed;
    }
}
</style>
